import React from 'react';
import { StarIcon, QuoteIcon } from 'lucide-react';
import type { Testimonial } from '../data/testimonials';
type TestimonialsSectionProps = {
  id: string;
  title: string;
  items: Testimonial[];
  tone?: 'light' | 'dark';
};
export function TestimonialsSection({
  id,
  title,
  items,
  tone = 'light'
}: TestimonialsSectionProps) {
  const isDark = tone === 'dark';
  const [featured, ...rest] = items;
  return <section className={`w-full py-16 sm:py-20 ${isDark ? 'bg-petrol text-white' : 'bg-cream text-body'}`} aria-labelledby={`${id}-title`} data-id="element-302" data-mp="1d1l60hnjsou3">
      <div className="mx-auto max-w-6xl px-5 sm:px-8" data-id="element-303" data-mp="1hyl4a6e5onan">
        <h2 id={`${id}-title`} className={`max-w-3xl font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl ${isDark ? 'text-white' : 'text-petrol'}`} data-id="element-304" data-mp="142hnpfw7gdgn">
          {title}
        </h2>

        <div className="mt-10 grid gap-5 lg:grid-cols-3" data-id="element-305" data-mp="32gj0lkaxz9n9">
          <figure className={`flex flex-col rounded-2xl p-7 sm:p-9 lg:col-span-3 ${isDark ? 'bg-petrol-soft' : 'bg-white shadow-sm'}`} data-id="element-306" data-mp="32fzsevb3fzmf">
            <QuoteIcon className="h-8 w-8 text-cta" aria-hidden="true" data-id="element-307" data-mp="2q1ydscjpmplw" />
            <blockquote className={`mt-4 max-w-3xl font-display text-xl font-bold leading-snug sm:text-2xl ${isDark ? 'text-white' : 'text-petrol'}`} data-id="element-308" data-mp="34rniuiuxbdrg">
              “{featured.quote}”
            </blockquote>
            <figcaption className="mt-auto pt-6" data-id="element-309" data-mp="273n7lto0b173">
              <Rating data-id="element-310" data-mp="274x4wgn1c5co" />
              <p className={`mt-2 text-sm font-semibold ${isDark ? 'text-white/75' : 'text-body-muted'}`} data-id="element-311" data-mp="275q6l37504ny">
                {featured.name} · {featured.location}
                {featured.detail ? ` · ${featured.detail}` : ''}
              </p>
            </figcaption>
          </figure>

          {rest.map(item => <TestimonialCard key={item.name} item={item} isDark={isDark} data-id="element-312" data-mp="3sgylk5vu3i88" />)}
        </div>
      </div>
    </section>;
}
function TestimonialCard({
  item,
  isDark
}: {
  item: Testimonial;
  isDark: boolean;
}) {
  return <figure className={`flex h-full flex-col rounded-2xl p-6 ${isDark ? 'bg-petrol-soft' : 'border border-beige-deep bg-white'}`} data-id="element-313" data-mp="8d2fiayji6a2">
      <blockquote className={`text-base leading-relaxed ${isDark ? 'text-white/85' : 'text-body'}`} data-id="element-314" data-mp="db3xsto561kh">
        “{item.quote}”
      </blockquote>
      <figcaption className="mt-auto pt-5" data-id="element-315" data-mp="3guggfmlh1kb9">
        <Rating data-id="element-316" data-mp="3grkgjsq2clk6" />
        <p className={`mt-2 text-sm font-semibold ${isDark ? 'text-white/75' : 'text-body-muted'}`} data-id="element-317" data-mp="3lsrnjpkdtgps">
          {item.name} · {item.location}
          {item.detail ? ` · ${item.detail}` : ''}
        </p>
      </figcaption>
    </figure>;
}
function Rating() {
  return <div className="flex gap-0.5" role="img" aria-label="5 de 5 estrellas" data-id="element-318" data-mp="3vjw42tpph9mq">
      {[0, 1, 2, 3, 4].map(star => <StarIcon key={star} className="h-4 w-4 fill-cta text-cta" aria-hidden="true" data-id="element-319" data-mp="2y1csmhuxq1ok" />)}
    </div>;
}